// `engine` already depends on `shared` (money.ts, types.ts, variance.ts, quote.ts all
// import from it) and `shared/passengers.ts` imports only `./service-schema.js`, so
// this direct import adds no cycle. Direct, not via a shared barrel: `shared/pricing-flatten.ts`
// imports BANDS from `../engine/index.js`, and a barrel hop would close that loop.
import { MAX_PASSENGERS_EXACT } from '../shared/passengers.js';

export type Band = '1-4' | '4-8' | '8-12' | '12-16';

export const BANDS = ['1-4', '4-8', '8-12', '12-16'] as const;

/**
 * The STORED over-band token (T1871 M5) — what `inquiries.passengers_band` and
 * `bookings.passengers_band` hold for a party the engine must never price.
 *
 * ⛔ EXPORTED SO NO CONSUMER HAND-SPELLS IT, and since T1871 M6 that is ENFORCED
 * rather than merely asked for. `scripts/check-copy.sh`'s enumeration rule still
 * bans only the four PRICED literals, so this token used to pass it silently
 * while being exactly the duplication that rule exists to stop; M6 added a
 * SECOND arm (`scan_over_band`) that walks the whole of `src` and fails on the
 * literal in any file but THIS one, in all three quote forms. Nine live
 * hand-spellings across `core/normalise.ts`, `server/inquiry.ts`,
 * `server/app.ts` and `bin/gen-site-inquiry-schema.ts` were the reason; all four
 * already imported from here, so nothing needed a new dependency.
 *
 * ⚠️ THAT ARM READS THIS LINE. It refuses to report clean if this file stops
 * spelling the literal (a rename, a move, a computed spelling), because a gate
 * whose owner has gone quiet passes over every file by having nothing to match.
 *
 * ⛔ IT IS NOT A MEMBER OF `BANDS`, and nothing here should ever add it:
 * `toCanonicalBand` maps it to `null` and `/api/quote` 400s on it, both
 * deliberately (docs/pricing-spec.md:97 — an over-band job is hand-priced by
 * judgment, never auto-repriced).
 *
 * `as const` is load-bearing: `BandRange.band` is `Band | '16+'`, so a widened
 * `string` here would not typecheck into the open row below.
 */
export const OVER_BAND = '16+' as const;

/**
 * Map both canonical forms (from pricing.json) and site-form variations
 * ("1 to 4", " 4 to 8 ", etc.) to a canonical Band.
 * "16+" and "16+ (we'll sort a coach)" → null (over-band, manual quote).
 */
export function toCanonicalBand(input: string): Band | null {
  const s = input.trim().toLowerCase();

  // Over-band
  if (s.startsWith('16+')) return null;

  // Normalise separator: "to" → "-", spaces around separator stripped
  const normalised = s
    .replace(/\s+to\s+/g, '-')  // "1 to 4" → "1-4"
    .replace(/\s*-\s*/g, '-')   // "1 - 4"  → "1-4"
    .trim();

  switch (normalised) {
    case '1-4':   return '1-4';
    case '4-8':   return '4-8';
    case '8-12':  return '8-12';
    case '12-16': return '12-16';
    default:      return null;
  }
}

/**
 * The count → band derivation, as DATA. (T1871 M1.)
 *
 * ⛔ THIS FILE IS THE HOME OF THE BAND LITERALS — AND THE HEADLINE IS SCOPED,
 * BECAUSE THE GATE IT CITES AS ITS AUTHORITY SANCTIONS ONE OTHER FILE (T1871 M7
 * correction (g)). `check-copy.sh`'s `COPY_ALLOW` exempts
 * `shared/pricing-flatten.ts` from this very rule: it owns the band-key to
 * pricing.json-column mapping, so it DEFINES those keys rather than duplicating
 * them. "Only legal home" was the sentence an editor acts on, and it was false
 * against the gate below it. Derive the exemption, never trust this sentence:
 * `grep -n -A8 'hand-duplicated domain enumeration' scripts/check-copy.sh`.
 * ⇒ this file is the DERIVATION's home, and any OTHER file that spells a band
 * literal must be in that allow-list with its reason.
 *
 * `scripts/check-copy.sh` bans `'1-4'|'4-8'|'8-12'|'12-16'` as a
 * "hand-duplicated domain enumeration" (its `checks[1]`), and that rule runs
 * over FOUR regions and no others: `src/client` and `src/shared` (the
 * `for root in (CLIENT, SHARED)` loop, minus the per-file exemptions in
 * `COPY_ALLOW`), then `src/notify` and `../packages/ui/src/email` (the two
 * `scan_copy_region(...)` calls under the summary prints, that function being
 * where the copy rules are applied to a region). The SITE regions are NOT in
 * that set — `scan_site` applies the dash rule only — which is why a count of
 * the file's regions overall comes out higher than four.
 * ⚠️ EVERY CITATION IN THIS PARAGRAPH IS STATEMENT-FIRST BECAUSE ITS FIVE LINE
 * NUMBERS ALL ROTTED AT ONCE (T1871 M7): the comment-lexer rewrite in the same
 * milestone moved `check-copy.sh` under them, and each one then resolved to
 * unrelated text while still reading as evidence. Derive them:
 * `grep -n 'for root in (CLIENT, SHARED)\|def scan_copy_region\|def scan_site\|scan_copy_region(' scripts/check-copy.sh`.
 * `src/engine` is in none of them, so the four PRICED literals below are
 * ungated here and gated everywhere they might be copied to. That is why the
 * table belongs here rather than in any consumer. Consumers import
 * `bandForCount`; they never re-spell a band.
 *
 * ⚠️ SINCE T1871 M6 THIS FILE IS SCANNED BY ONE ARM AND STILL NOT BY `checks[1]`,
 * and the distinction is the whole design. `scan_over_band` walks all of `src`
 * for the OVER-BAND token and exempts exactly this file (see `OVER_BAND`'s own
 * header); the priced-band enumeration rule is unchanged and still runs over the
 * four regions named above. Derive it rather than trusting either sentence:
 * `grep -n "OVER_BAND_OWNER\|OVER_BAND_ROOT" scripts/check-copy.sh`.
 *
 * The intervals are HALF-OPEN — `[min, maxExclusive)` — so adjacency is the
 * plain identity `ranges[i].maxExclusive === ranges[i + 1].min`, and no `+ 1` is
 * ever applied to a passenger count in this file for an off-by-one to hide in.
 * (`i + 1` indexes the next ROW. `MAX_PASSENGERS_EXACT + 1` is not CODE
 * anywhere in this file — but it is not "only in the spec" either: it is written
 * in the prose of `bandForCount`'s own header below, as well as in the spec's
 * refusal row. True as a code census, misleading as it was written (T1871 M7
 * correction (c)); derive it rather than trusting either form:
 * `grep -n 'MAX_PASSENGERS_EXACT + 1' src/engine/bands.ts test/engine/bands.spec.ts`.
 * Neither occurrence shifts a boundary.)
 *
 * ⚠️ WHAT THE LOAD-TIME ASSERTION BELOW DOES AND DOES NOT CATCH. It catches
 * gaps, overlaps, a re-ordered or re-labelled table — the priced rows by
 * `priced === BANDS`, and the OPEN row by its own `band !== '16+'` clause,
 * which exists because `priced` filters that row out and would otherwise leave
 * its token unchecked — a row whose own bounds are inverted, more than one open
 * row, an open row that is not last — and, because
 * it anchors TWO ABSOLUTE POINTS (`min === 1` and the first seam `=== 5`), a
 * UNIFORM shift of every boundary. That last one is the mistake these tokens
 * invite: `1-4` and `4-8` both contain "4", so the whole table reads plausibly
 * one lower (`1-4` = 1..3, `4-8` = 4..7, … `16+` = 16..). Every clause except
 * the two anchors is RELATIVE, so without them that inversion passes all of
 * them and ships. It still does NOT catch a NON-UNIFORM shift — moving only an
 * interior seam keeps contiguity, both anchors and the label order intact. That
 * one is caught by the SPEC rather than here — and by TWO arms of it, not one
 * (T1871 M7 correction (h), an under-claim in the sentence this replaces):
 * `COUNT_TO_BAND` pins the complete map over 0..18, so `[4, '1-4']` and
 * `[16, '12-16']` are the anti-inversion guard, and the `has exactly the
 * intended half-open bounds` arm beside it asserts all five `[min, maxExclusive)`
 * pairs as absolute literals, which a moved interior seam also fails. Both are in
 * `test/engine/bands.spec.ts`. ⛔ Thin either and that much of the guard goes
 * with it.
 *
 * The band strings are DISPLAY/PRICING TOKENS. Nothing here parses digits out
 * of them; the numbers live in `min`/`maxExclusive` and only there. (Board
 * #1871's wording, in counts: 1-4 = 1..4, 4-8 = 5..8, 8-12 = 9..12,
 * 12-16 = 13..16, 17+ over-band. `'16+'` is the STORED token for that last row;
 * `17` is where the count range starts. The two disagree on purpose.)
 *
 * ⛔ THE INVERSE IS DELIBERATELY NOT FOLDED IN HERE. `shared/passengers.ts`'s
 * `bandToPax` maps band → representative ceiling and has exactly two callers,
 * both writing `bookings.passengers`. Folding it into this table would have to
 * restate `'16+' → 16`, which is a lie about a 40-seat coach: this table's top
 * row is open (`maxExclusive: null`) precisely because the over-band has no
 * ceiling. Leave `bandToPax` alone.
 */
export type BandRange = {
  /** The pricing/display token. Never parsed for digits. */
  readonly band: Band | '16+';
  /** First count in the range, inclusive. */
  readonly min: number;
  /** First count PAST the range; `null` on the one open-topped row. */
  readonly maxExclusive: number | null;
};

/**
 * ⚠️ FROZEN, not merely `readonly`. `readonly` is erased at compile time, and
 * this table is exported into the server AND the browser bundle, so without
 * `Object.freeze` any importer could mutate a row after the assertion below has
 * run — and that assertion is what `bandForCount` cites to justify its non-null
 * return. Freezing makes the checked shape hold for the process, not just for
 * the instant of import.
 */
export const BAND_RANGES: readonly BandRange[] = Object.freeze([
  Object.freeze<BandRange>({ band: '1-4', min: 1, maxExclusive: 5 }),
  Object.freeze<BandRange>({ band: '4-8', min: 5, maxExclusive: 9 }),
  Object.freeze<BandRange>({ band: '8-12', min: 9, maxExclusive: 13 }),
  Object.freeze<BandRange>({ band: '12-16', min: 13, maxExclusive: 17 }),
  Object.freeze<BandRange>({ band: OVER_BAND, min: 17, maxExclusive: null }),
]);

/**
 * Contiguity, sane bounds, totality and TWO ABSOLUTE ANCHORS, asserted at module
 * load. This is what earns `bandForCount` its non-null return over
 * `1..MAX_PASSENGERS_EXACT`: a gap, an overlap, an inverted row, a second open
 * row, a re-ordered table or a uniform shift of every boundary is a crash at
 * import — the loudest, earliest place — rather than a silently mis-banded
 * quote. See the header above for the one case this does NOT reach (a
 * non-uniform shift) and which spec table does.
 */
(function assertBandRangesAreContiguousAndTotal(): void {
  const first = BAND_RANGES[0];
  // ⛔ BOTH anchors are ABSOLUTE on purpose. Every other clause here is
  // relative, so a uniform shift of all five rows satisfies all of them; it is
  // these two literals — the floor and the first seam — that refuse it. 1 is
  // the smallest real party; 5 is the first count that is NOT in '1-4'.
  if (first === undefined || first.min !== 1 || first.maxExclusive !== 5) {
    throw new Error('BAND_RANGES must start at [1, 5): the whole table has shifted');
  }
  for (let i = 0; i < BAND_RANGES.length - 1; i += 1) {
    const here = BAND_RANGES[i]!;
    const next = BAND_RANGES[i + 1]!;
    if (here.maxExclusive !== null && here.maxExclusive <= here.min) {
      throw new Error(`BAND_RANGES row ${i} (${here.band}) is empty or inverted: [${here.min}, ${here.maxExclusive})`);
    }
    if (here.maxExclusive !== next.min) {
      throw new Error(
        `BAND_RANGES is not contiguous at row ${i}: ${here.band} ends ${here.maxExclusive}, ${next.band} starts ${next.min}`,
      );
    }
  }
  const open = BAND_RANGES.filter((r) => r.maxExclusive === null);
  // ⛔ THE `band !== '16+'` CLAUSE IS LOAD-BEARING, not belt-and-braces. The
  // `priced` clause below filters `maxExclusive !== null`, which EXCLUDES this
  // row — so without this, the open row's token is checked by nothing ELSE IN
  // THIS ASSERTION, and re-labelling it '12-16' or '1-4' would pass every other
  // clause here and send every party of 17+ to a priced band. It is the only
  // check this token has AT LOAD TIME, in the process, before a test runs.
  // ⚠️ SCOPED ON PURPOSE (T1871 M7 correction (e)). The unscoped forms this
  // replaces — "checked by nothing at all", "its only spec-side check" — were
  // FALSE: a re-label also reds four arms of `test/engine/bands.spec.ts`, as the
  // note below this clause sets out. Scoped to this assertion the claim holds;
  // unscoped it does not, and an unscoped claim about coverage is the shape that
  // gets a real check deleted as redundant.
  // ⛔ T1871 M5: THE LITERAL HERE STAYS A LITERAL, deliberately, now that the
  // row above is built from `OVER_BAND`. Writing `OVER_BAND` on both sides would
  // turn this clause into `x !== x` — a guard that can never fire — and the
  // constant would then be re-spellable with nothing IN THIS ASSERTION to catch
  // it. Two independent spellings, one of them absolute, is the mechanism here.
  // ⚠️ SCOPED, NOT ABSOLUTE (T1871 M5 review; M7 correction (e) then scoped the
  // paragraph above in the same terms). "Nothing at all" would be FALSE: a
  // re-spelling also reds `test/engine/bands.spec.ts` — the [17, '16+'],
  // [18, '16+'] and [MAX_PASSENGERS_EXACT, '16+'] rows of its derivation table,
  // and its "labels the open row with the over-band token" arm, all of which
  // spell the token themselves. What is true is narrower and is the reason this
  // clause exists: WITHOUT IT, this LOAD-TIME assertion — the one that runs in
  // every process before a single test does — would grade the open row's token
  // against itself and pass on any spelling.
  if (
    open.length !== 1 ||
    open[0] !== BAND_RANGES[BAND_RANGES.length - 1] ||
    open[0]!.band !== '16+'
  ) {
    throw new Error("BAND_RANGES must have exactly one open-topped row, it must be last, and it must be '16+'");
  }
  const priced = BAND_RANGES.filter((r) => r.maxExclusive !== null).map((r) => r.band);
  if (priced.length !== BANDS.length || priced.some((b, i) => b !== BANDS[i])) {
    throw new Error(`BAND_RANGES priced rows ${priced.join(',')} do not match BANDS ${BANDS.join(',')}`);
  }
})();

/**
 * The one count → band derivation in this repo. Returns the over-band token
 * `'16+'` above the priced rows, and `null` for anything that is not a real
 * headcount.
 *
 * The integer guard is NOT tidiness: the table comparison alone would map 4.5
 * into `'1-4'`, so a fractional headcount would price as a real one. `NaN`,
 * `Infinity`, `-1`, `0` and `MAX_PASSENGERS_EXACT + 1` all refuse here.
 *
 * The upper bound reuses `MAX_PASSENGERS_EXACT` — the same constant the quote
 * door and the Quote screen bound `bookings.passengers_exact` with. A second
 * ceiling here is precisely the drift that constant's own header refuses.
 *
 * ⛔ ABOVE `MAX_PASSENGERS_EXACT` THIS RETURNS `null`, NOT `'16+'`, AND THAT IS
 * DELIBERATE — do not "fix" it to match the literal reading of "17 or more".
 * The top row is open so that a real coach party has a band, but a count past
 * the ceiling is not a big party, it is a typo or a probe: the same value the
 * quote door refuses outright. Banding it as `'16+'` would hand a caller a
 * usable band for a headcount the write path will reject, which is the
 * client-bound-disagrees-with-server-bound failure `MAX_PASSENGERS_EXACT`'s own
 * header was written to prevent. Fail closed and let the caller refuse.
 */
export function bandForCount(n: number): Band | '16+' | null {
  if (!Number.isSafeInteger(n) || n < 1 || n > MAX_PASSENGERS_EXACT) return null;
  // One comparison shape for every row, including the open top.
  const row = BAND_RANGES.find((r) => n >= r.min && (r.maxExclusive === null || n < r.maxExclusive));
  // The load-time assertion above makes this exhaustive over the guarded range;
  // the fallback exists for the type, not for a reachable case.
  return row === undefined ? null : row.band;
}
